package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.whiteSpace
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Standard badge color variants with subtle borders for better contrast against backgrounds
 */
enum class BadgeColor(val background: String, val text: String, val border: String) {
    GREEN("#e8f5e9", "#2e7d32", "#c8e6c9"),
    RED("#ffebee", "#c62828", "#ffcdd2"),
    BLUE("#e3f2fd", "#1565c0", "#bbdefb"),
    ORANGE("#fff3e0", "#e65100", "#ffe0b2"),
    YELLOW("#fffde7", "#f57f17", "#fff9c4"),
    GRAY("#f5f5f5", "#616161", "#e0e0e0")
}

/**
 * A generic badge/chip component that can display arbitrary composable content.
 * Use this version when you need custom content such as icons, multiple text
 * elements, or custom layouts inside a badge.
 *
 * @param color The badge color variant (default: GRAY)
 * @param fontSize The font size for text content (default: 11px)
 * @param content The composable content to display inside the badge
 */
@Composable
fun Badge(
    color: BadgeColor = BadgeColor.GRAY,
    fontSize: CSSSizeValue<CSSUnit.px> = 11.px,
    content: @Composable () -> Unit
) {
    Span({
        style {
            this.fontSize(fontSize)
            padding(2.px, 8.px)
            borderRadius(4.px)
            backgroundColor(Color(color.background))
            color(Color(color.text))
            border(1.px, LineStyle.Solid, Color(color.border))
            whiteSpace("nowrap")
            fontWeight(500)
        }
    }) {
        content()
    }
}

/**
 * A compact badge/chip component for displaying labels, tags, or status indicators.
 * This is a convenience overload for simple text-only badges.
 *
 * @param text The text to display in the badge
 * @param color The badge color variant (default: GRAY)
 * @param fontSize The font size (default: 11px)
 */
@Composable
fun Badge(
    text: String,
    color: BadgeColor = BadgeColor.GRAY,
    fontSize: CSSSizeValue<CSSUnit.px> = 11.px
) {
    Badge(color = color, fontSize = fontSize) {
        Text(text)
    }
}